import * as Sentry from "@sentry/react";
import { format, utcToZonedTime } from "date-fns-tz";
import { postApi, getJsonApi } from "./AmplifyServices";
import moment from "moment";
import { addMinutes, isValid } from "date-fns";
import { enUS } from "date-fns/locale";
const getTimezoneIdByGeoLoc = async ({ lat, lng }) => {
    try {
        const stamp = Math.floor(Date.now() / 1000);
        const tzidResp = await getJsonApi("mapdirection", "/tzid", {
            queryParams: {
                lat,
                lng,
                stamp
            }
        });
        if (tzidResp && tzidResp.success) return tzidResp.tzid;
        else return Intl.DateTimeFormat().resolvedOptions().timeZone; //return browser tz
    } catch (err) {
        Sentry.captureException(err);
        return Intl.DateTimeFormat().resolvedOptions().timeZone; //return browser tz
    }
};

async function getBookingDateDescription(apptDateStr, targetTz, durationMins) {
    const apptDate = new Date(apptDateStr); //work with the copy
    let tzAbbrv = format(apptDate, "z", {
        timeZone: isValidTimeZone(targetTz)
            ? targetTz
            : Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    if (tzAbbrv && (tzAbbrv.includes("GMT+") || tzAbbrv.includes("GMT-"))) {
        const result = await postApi("mapdirection", "/timezone", {
            body: {
                action: "formatInLocationTz",
                dateInfo: {
                    datetime: getDateParts(apptDate),
                    tz: targetTz,
                    durationMins
                }
            }
        });
        if (result && result.success && result.dateInfo) {
            return result.dateInfo;
        }
    } else {
        return createTimeDisplayInfoForTimeZone(
            apptDateStr,
            targetTz,
            durationMins
        );
    }
}

function getDateParts(d) {
    return {
        year: d.getFullYear(),
        month: d.getMonth(),
        date: d.getDate(),
        hours: d.getHours(),
        minutes: d.getMinutes()
    };
}

function getFullDateDisplayWithGMTOffset(dateInfo) {
    let gmtoff = dateInfo.tz_gmtoff ? `(${dateInfo.tz_gmtoff})` : "";
    return `${dateInfo.dt_full_disp} ${gmtoff}`;
}

function getSlotDisplayWithGMTOffset(dateInfo) {
    let gmtoff = dateInfo.tz_gmtoff ? `(${dateInfo.tz_gmtoff})` : "";
    return `${dateInfo.en_slot_disp} ${gmtoff}`;
}

function getEndTimeDisplayWithGMTOffset(dateInfo) {
    let gmtoff = dateInfo.tz_gmtoff ? `(${dateInfo.tz_gmtoff})` : "";
    let tz_abbr = dateInfo.tz_abbr_disp ? dateInfo.tz_abbr_disp : "";
    return `${dateInfo.tm_et_disp} ${tz_abbr} ${gmtoff}`;
}
export const createTimeDisplayInfoForTimeZone = (
    dateTimeString,
    targetTz,
    durationMins
) => {
    const convertedDateTime = utcToZonedTime(dateTimeString, targetTz);
    return {
        dtstamp_str: format(convertedDateTime, "yyyy-MM-dd'T'HH:mm:ssXXX", {
            timeZone: targetTz
        }),
        tz_abbr_disp: format(convertedDateTime, "z", { timeZone: targetTz }),
        tz_gmtoff: `GMT${format(convertedDateTime, "XXX", { timeZone: targetTz })}`,
        dt_disp: format(convertedDateTime, "yyyy-MM-dd", {
            timeZone: targetTz
        }),
        dt_long_disp: format(convertedDateTime, "EEEE, MMMM d, yyyy", {
            timeZone: targetTz
        }),
        dt_full_disp: format(convertedDateTime, "EEE, MMM d — h:mm a (zzz)", {
            timeZone: targetTz
        }),
        en_slot_disp: `${format(
            convertedDateTime,
            "EEE, MMM d 'at' h:mm a zzz",
            { timeZone: targetTz }
        )}`,
        tm_st_disp: format(convertedDateTime, "h:mm a", { timeZone: targetTz }),
        tm_et_disp: format(
            convertedDateTime.setMinutes(
                convertedDateTime.getMinutes() + durationMins
            ),
            "h:mm a",
            { timeZone: targetTz }
        ),
        durationMins: durationMins,
        tz: targetTz
    };
};

export const isValidTimeZone = (timeZone) => {
    try {
        format(new Date(), { timeZone });
        return true;
    } catch {
        return false;
    }
};

/**
 * Function to construct a dateInfo object into a specific timezone from a Date object
 * @param {*} dateObject
 * @param {*} durationMins
 * @param {*} targetTimezone
 * @returns
 */
function formatDateInfoAttributes(dateObject, durationMins, targetTimezone) {
    //moment object from the Date
    const momentDate = moment(dateObject);
    const momentDateForTz = moment(dateObject).tz(targetTimezone);
    const tzAbbr = momentDateForTz.format("z");
    /* const tzAbbrLong = getFullTimeZoneNameForTimeZone(
        dateObject,
        targetTimezone,
        enUS
    ); */
    const tzOffset = momentDateForTz.format("Z");

    //formatted attributes
    const dateInfo = {
        dt_disp: momentDate.format("YYYY-MM-DD"), // "2024-10-06"
        dt_full_disp: `${momentDate.format("ddd, MMM D — h:mm A")} (${tzAbbr})`, // "Sun, Oct 6 — 11:00 AM (EDT)"
        dt_long_disp: momentDate.format("dddd, MMMM D, YYYY"), // "Sunday, October 6, 2024"
        dtstamp_str: `${momentDate.format("YYYY-MM-DDTHH:mm:ss")}${tzOffset}`, // "2024-10-06T11:00:00-04:00"
        tm_st_disp: momentDate.format("h:mm A"), //12:00 PM
        ...(durationMins
            ? {
                  tm_et_disp: momentDate
                      .clone()
                      .add(durationMins, "minutes")
                      .format("h:mm A")
              }
            : {}), //11:00 AM
        label: momentDate.format("HH:mm"), //24-hour time 14:00
        tz: targetTimezone, // "America/Toronto"
        tz_abbr_disp: tzAbbr, //EDT
        tz_gmtoff: `GMT${tzOffset}`, //GMT-04:00
        ...(durationMins ? { durationMins: durationMins } : {}), //service duration in minutes
        st_slot_disp: `${momentDate.format(
            "ddd, MMM D [at] h:mm A z"
        )}${tzAbbr}`, //Sat, Oct 5 at 10:00 AM EDT
        ...(durationMins
            ? {
                  en_slot_disp: `${momentDate
                      .clone()
                      .add(durationMins, "minutes")
                      .format("ddd, MMM D [at] h:mm A z")}${tzAbbr}`
              }
            : {}) //Sat, Oct 5 at 11:00 AM EDT
    };

    return dateInfo;
}

function isSameTimezone(timezone1, timezone2, date = new Date()) {
    // meaning the timezone offset is same or not
    const time1 = new Date(date).toLocaleTimeString("en-US", {
        timeZone: timezone1
    });
    const time2 = new Date(date).toLocaleTimeString("en-US", {
        timeZone: timezone2
    });
    return time1 === time2;
}

async function getBookingDateDescriptionInTimezone(
    apptdate,
    timeZone,
    durationMins
) {
    // this function(original version before migration) preseves the date/time part and changes the timezone part
    apptdate = new Date(apptdate); //work with the copy
    let tzAbbrv = format(apptdate, "z", { timeZone });
    console.log("tzAbbrv", tzAbbrv);
    if (tzAbbrv && (tzAbbrv.includes("GMT+") || tzAbbrv.includes("GMT-"))) {
        const result = await postApi("mapdirection", "/timezone", {
            body: {
                action: "formatInLocationTz",
                dateInfo: {
                    datetime: getDateParts(apptdate),
                    tz: timeZone,
                    durationMins
                }
            }
        });
        if (result && result.success && result.dateInfo) {
            console.log("formatInLocationTz", JSON.stringify(result));
            return result.dateInfo;
        }
    } else {
        let tz = { timeZone };
        const toDateTime = new Date(apptdate);
        toDateTime.setMinutes(apptdate.getMinutes() + durationMins);
        return {
            dtstamp_str: format(apptdate, "yyyy-MM-dd'T'HH:mm:ssXXX", tz),
            tz_abbr_disp: format(apptdate, "z", tz),
            tz_gmtoff: `GMT${format(apptdate, "XXX", tz)}`,
            dt_disp: format(apptdate, "yyyy-MM-dd", tz),
            dt_long_disp: format(apptdate, "EEEE, MMMM d, yyyy", tz),
            dt_full_disp: format(apptdate, "EEE, MMM d, yyyy h:mm a zzz", tz),
            en_slot_disp: `${format(
                apptdate,
                "EEE, MMM d 'at' h:mm a zzz",
                tz
            )}`,
            tm_st_disp: format(apptdate, "h:mm a", tz),
            tm_et_disp: format(toDateTime, "h:mm a", tz),
            durationMins: durationMins,
            tz: timeZone,
            startTime: format(apptdate, "HH:mm", tz),
            endTime: format(toDateTime, "HH:mm", tz)
        };
    }
}

/**
 * Adds minutes to a 24-hour time string (HH:mm) using date-fns.
 * @param timeString - The time string in "HH:mm" format (e.g., "13:00", "09:30").
 * @param minutesToAdd - The number of minutes to add (can be positive or negative).
 * @returns A new time string in "HH:mm" format, or null if the input is invalid.
 */
const addMinutesToTimeString = (timeString, minutesToAdd) => {
    if (!/^([0-9]{2}):([0-9]{2})$/.test(timeString)) {
        return null; // Invalid time string format
    }

    const [hours, minutes] = timeString.split(":").map(Number);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
    }
    const baseDate = new Date(0, 0, 0, hours, minutes);

    if (!isValid(baseDate)) {
        return null;
    }

    const newDate = addMinutes(baseDate, minutesToAdd);
    return format(newDate, "HH:mm");
};

/**
 * Extracts and returns only the full, localized name of the time zone from a Date object's formatted string.
 *
 * @param {Date} date The Date object for which to get the time zone name.
 * @param {string} [locale='en-US'] The BCP 47 language tag to use for localization (e.g., 'en-US', 'fr-CA').
 * @returns {string} The full time zone name (e.g., "Eastern Daylight Time", "Pacific Standard Time").
 */
function getFullTimeZoneName(date, locale = "en-US") {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error(
            "Invalid Date object provided to getFullTimeZoneNameOnlyExtracted."
        );
    }

    const options = {
        day: "numeric",
        timeZoneName: "long"
    };

    try {
        const formatter = new Intl.DateTimeFormat(locale, options);
        const formattedString = formatter.format(date);

        const commaIndex = formattedString.indexOf(",");
        if (commaIndex !== -1) {
            return formattedString.substring(commaIndex + 1).trim();
        } else {
            console.warn(
                "No comma found in formatted date string. Returning full string:",
                formattedString
            );
            return formattedString;
        }
    } catch (error) {
        console.error("Error getting full time zone name:", error);
        return ""; // Or throw error;
    }
}

/**
 * Returns the full, localized time zone name for a specific date and IANA time zone identifier.
 * This handles Daylight Saving Time changes correctly.
 *
 * @param {Date} date The Date object (its UTC time will be used as the reference point).
 * @param {string} ianaTimeZone The IANA time zone identifier (e.g., 'America/Toronto', 'Europe/London').
 * @param {string} [locale='en-US'] The BCP 47 language tag to use for localization.
 * @returns {string} The full time zone name (e.g., "Eastern Daylight Time", "Eastern Standard Time").
 */
function getFullTimeZoneNameForTimeZone(date, ianaTimeZone, locale = "en-US") {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error("Invalid Date object provided.");
    }
    if (typeof ianaTimeZone !== "string" || !ianaTimeZone.trim()) {
        throw new Error("Invalid IANA time zone identifier provided.");
    }

    const options = {
        timeZoneName: "long",
        timeZone: ianaTimeZone,
        day: "numeric"
    };

    try {
        const formatter = new Intl.DateTimeFormat(locale, options);
        const formattedString = formatter.format(date);

        const commaIndex = formattedString.indexOf(",");
        if (commaIndex !== -1) {
            return formattedString.substring(commaIndex + 1).trim();
        } else {
            const parts = formatter.formatToParts(date);
            const timeZonePart = parts.find(
                (part) => part.type === "timeZoneName"
            );
            if (timeZonePart) {
                return timeZonePart.value;
            }
            console.warn(
                `Could not extract time zone name from "${formattedString}". Returning full string.`
            );
            return formattedString;
        }
    } catch (error) {
        console.error(
            `Error getting time zone name for ${ianaTimeZone}:`,
            error
        );
        return "";
    }
}

export {
    getTimezoneIdByGeoLoc,
    getFullDateDisplayWithGMTOffset,
    getEndTimeDisplayWithGMTOffset,
    getSlotDisplayWithGMTOffset,
    getBookingDateDescription,
    getFullTimeZoneName,
    getFullTimeZoneNameForTimeZone,
    formatDateInfoAttributes,
    isSameTimezone,
    getBookingDateDescriptionInTimezone,
    addMinutesToTimeString
};
